import { Routes, UrlMatcher, UrlSegment } from '@angular/router';
import { inject } from '@angular/core';
import { pageDataResolver } from './core/resolvers/resolver-resolver';
import { countriesResolver } from './core/resolvers/countries.resolver';
import { isStateCodeGuard } from './core/guards/is-state-code.guard';
import { rootRedirectGuard } from './core/guards/root-redirect.guard';
import { cartGuard } from './core/guards/cart.guard';
import { invoiceGuard } from './core/guards/invoice.guard';
import { authGuard } from './core/guards/auth.guard';
import { UsStatesService } from './core/services/us-states.service';


/** Matches a single URL segment like `expedited-apostille-services-egypt` and exposes `:country`. */
export const expeditedApostilleMatcher: UrlMatcher = (segments) => {
    if (segments.length !== 1) return null;
    const match = /^expedited-apostille-services-(.+)$/i.exec(segments[0].path);
    return match
        ? { consumed: segments, posParams: { country: new UrlSegment(match[1], {}) } }
        : null;
};

/** Matches a single URL segment like `legalization-services-iran` and exposes `:country`. */
export const legalizationServicesMatcher: UrlMatcher = (segments) => {
    if (segments.length !== 1) return null;
    const match = /^legalization-services-(.+)$/i.exec(segments[0].path);
    return match
        ? { consumed: segments, posParams: { country: new UrlSegment(match[1], {}) } }
        : null;
};

/** Matches a URL like `maryland-apostille` and exposes `:stateName`. */
export const stateApostilleMatcher: UrlMatcher = (segments) => {
    if (segments.length !== 1) return null;
    const path = decodeURIComponent(segments[0].path);
    const match = /^(.+)-apostille$/i.exec(path);
    return match
        ? { consumed: segments, posParams: { stateName: new UrlSegment(match[1], {}) } }
        : null;
};

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./features/home/home').then(m => m.Home),
        resolve: { 
            data: pageDataResolver,
            countries: countriesResolver
        }
    },
    {
        path: 'home',
        loadComponent: () => import('./features/home/home').then(m => m.Home),
        resolve: { 
            data: pageDataResolver,
            countries: countriesResolver
        }
    },
    {
        path: 'pay',
        loadComponent: () => import('./features/pay/pay').then(m => m.Pay)
    },
    // {
    //     path: 'select-country',
    //     loadComponent: () => import('./features/select-country/select-country').then(m => m.SelectCountry),
    //     resolve: {
    //         countries: countriesResolver
    //     }
    // },
    // {
    //     path: 'countries',
    //     loadComponent: () => import('./features/select-country/select-country').then(m => m.SelectCountry),
    //     resolve: {
    //         countries: countriesResolver
    //     }
    // },

    {
        path: 'contact',
        loadComponent: () => import('./features/contact/contact/contact').then(m => m.Contact),
    },
    {
        path: 'states',
        loadComponent: () => import('./features/states/states').then(m => m.StatesPage),
    },
    {
        path: 'apostille-authentication-document-legalization',
        loadComponent: () => import('./features/apostille-document/apostille-document').then(m => m.ApostilleDocumentPage),
    },
    {
        path: 'document-legalization-authentication-attestation',
        loadComponent: () => import('./features/document-legalization/document-legalization').then(m => m.DocumentLegalizationPage),
    },
    {
        path: 'digital-document-apostille',
        loadComponent: () => import('./features/digital-document-apostille/digital-document-apostille').then(m => m.DigitalDocumentApostillePage),
    },
    {
        path: 'apostille-fbi-background-check',
        loadComponent: () => import('./features/fbi/fbi').then(m => m.FbiPage),
    },
    {
        path: 'us-department-state-apostille',
        loadComponent: () => import('./features/department/department').then(m => m.DepartmentPage),
    },
    {
        path: 'declaration-of-conformity',
        loadComponent: () => import('./features/conformity/conformity').then(m => m.ConformityPage),
    },
    {
        path: 'history',
        loadComponent: () => import('./features/history/history').then(m => m.HistoryComponent),
        canActivate: [authGuard]
    },
    {
        path: 'account-history',
        loadComponent: () => import('./features/history/history').then(m => m.HistoryComponent),
        canActivate: [authGuard]
    },
    {
        path: 'in-process',
        loadComponent: () => import('./features/in-process/in-process').then(m => m.InProcessComponent),
        canActivate: [authGuard]
    },
    {
        path: 'unpaid-invoices',
        loadComponent: () => import('./features/unpaid-invoices/unpaid-invoices').then(m => m.UnpaidInvoicesComponent),
        canActivate: [authGuard]
    },
    {
        path: 'discharged',
        loadComponent: () => import('./features/discharged/discharged').then(m => m.DischargedComponent),
        canActivate: [authGuard]
    },
    {
        path: 'cart',
        loadComponent: () => import('./features/order/cart/cart').then(m => m.Cart),
        canActivate: [cartGuard]
    },
    {
        path: 'order-form',
        loadComponent: () => import('./features/order/order-form/order-form').then(m => m.OrderForm),
        canActivate: [cartGuard]
    },
    {
        path: 'shipping-cross',
        loadComponent: () => import('./features/order/shipping-cross/shipping-cross').then(m => m.ShippingCrossComponent),
        canActivate: [cartGuard]
    },
    {
        path: 'shipping',
        loadComponent: () => import('./features/order/shipping/shipping').then(m => m.ShippingComponent),
        canActivate: [cartGuard]
    },
    {
        path: 'payment',
        loadComponent: () => import('./features/order/payment/payment').then(m => m.Payment),
        canActivate: [cartGuard]
    },
    {
        path: 'invoice',
        loadComponent: () => import('./features/order/invoice/invoice').then(m => m.Invoice),
        canActivate: [invoiceGuard]
    },
    {
        path: 'thank-you',
        loadComponent: () => import('./features/order/thank-you/thank-you').then(m => m.ThankYouComponent)
    },
    {
        path: 'create-envelope',
        loadComponent: () => import('./features/fedex/fedex').then(m => m.Fedex)
    },
    {
        path: ':stateCode/:slug',
        canMatch: [isStateCodeGuard],
        loadComponent: () => import('./features/dynamic-pages/dynamic-pages').then(m => m.DynamicPages),
        resolve: {
            data: pageDataResolver,
            stateDetails: (route: import('@angular/router').ActivatedRouteSnapshot) => {
                const code = route.params['stateCode'];
                return inject(UsStatesService).loadStateDetails(code);
            }
        },
        runGuardsAndResolvers: 'always'
    },
    // {
    //     path: ':stateCode',
    //     canMatch: [isStateCodeGuard],
    //     loadComponent: () => import('./features/state-page/state-page').then(m => m.StatePage),
    //     resolve: {
    //         stateDetails: (route: import('@angular/router').ActivatedRouteSnapshot) => {
    //             const code = route.params['stateCode'];
    //             return inject(UsStatesService).loadStateDetails(code);
    //         }
    //     }
    // },
    {
        matcher: stateApostilleMatcher,
        loadComponent: () => import('./features/state-apostille/state-apostille').then(m => m.StateApostillePage),
    },
    {
        matcher: expeditedApostilleMatcher,
        loadComponent: () => import('./features/country-apostille/country-apostille').then(m => m.CountryApostillePage),
    },
    {
        matcher: legalizationServicesMatcher,
        loadComponent: () => import('./features/country-legalization/country-legalization').then(m => m.CountryLegalizationPage),
    },
    {
        path: '404',
        loadComponent: () => import('./features/not-found/not-found').then(m => m.NotFound),
    },
    {
        path: '**',
        loadComponent: () => import('./features/dynamic-pages/dynamic-pages').then(m => m.DynamicPages),
        resolve: { data: pageDataResolver },
        runGuardsAndResolvers: 'always'
    }
];
